import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Calendar,
  CalendarX,
  Check,
  Clock,
  MoreHorizontal,
  Pencil,
  Plus,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import { PatientTabIconButton } from '@/components/patient/PatientTabActions';
import {
  CourseBreadcrumb,
  CourseCount,
  CourseIntervalStrip,
  CourseMetaChip,
  CourseSegmentBar,
  CourseStatusChip,
  CourseStepMarker,
  CoursePlannedBookingChip,
  CourseTabPanel,
  CourseTodayChip,
  CourseVisualChip,
} from '@/components/patient/course/CourseChrome';
import { canCancelBookedStep, canEditBookedCourse } from '@/lib/bookedTreatmentCourse';
import {
  deriveBookedByChannel,
  deriveCourseStepVisual,
  deriveQuestionnaireState,
  courseProgressLabel,
} from '@/lib/courseStepStatus';
import {
  deriveCourseActionItems,
  stepHasAction,
  UNANSWERED_QUESTIONNAIRES_REQUIRE_ACTION,
} from '@/lib/courseRequiresAction';
import {
  formatCourseDate,
  formatCourseDateTime,
  formatCourseDateTimeRange,
  formatCourseDateTimeWithToday,
  formatCoursePeriod,
  isSameCourseDay,
} from '@/lib/courseDateFormat';
import { isInstantInCourseWindow } from '@/lib/courseIntervalWindow';
import {
  clinicMemberNameById,
  courseActionItemMeta,
  courseActionTitle,
  courseCreatedByClause,
  courseIntervalAfterLabel,
  coursePeriod,
  coursePersonNames,
  coursePractitionerNames,
  courseQuestionnaireName,
  courseStepTitle,
  courseStepWindow,
  nextCourseStep,
  questionnaireStatusLabel,
} from '@/lib/courseUiHelpers';
import type { StaffCourseStep, StaffTreatmentCourse } from '@/lib/treatmentCourseStaffView';

export function TreatmentCourseOverview({
  course,
  canEdit,
  onBack,
  onOpenStep,
  onEditCourse,
  onCancelCourse,
  onAddStep,
  onCancelStep,
  onRemoveStep,
  onWriteJournal,
  onRemindQuestionnaire,
}: {
  course: StaffTreatmentCourse;
  canEdit: boolean;
  onBack: () => void;
  onOpenStep: (stepId: string) => void;
  onEditCourse: (stepId?: string) => void;
  onCancelCourse?: () => void;
  onAddStep?: () => void;
  onCancelStep?: (step: StaffCourseStep) => void;
  onRemoveStep?: (step: StaffCourseStep) => void;
  onWriteJournal?: (stepId: string) => void;
  onRemindQuestionnaire?: (step: StaffCourseStep, templateId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: members } = useClinicMembers(course.clinic_id);
  const memberNames = useMemo(() => clinicMemberNameById(members), [members]);
  const progress = courseProgressLabel(course.steps);
  const next = nextCourseStep(course.steps);
  const broken = new Set(
    course.interval_warnings.filter((row) => row.is_broken).map((row) => row.step_id),
  );
  const actions = useMemo(
    () => deriveCourseActionItems(course.steps, broken),
    [broken, course.steps],
  );
  const editable = canEditBookedCourse(course.status, canEdit);
  const period = coursePeriod(course.steps);
  const practitioners = coursePractitionerNames(course.steps);
  const actionSteps = new Set(actions.map((item) => item.sortOrder));
  const actionDetail =
    actions.length === 0
      ? t('treatment_courses.none')
      : actionSteps.size === 1 && actions.length === 2
        ? t('treatment_courses.stat_action_on_step', { n: [...actionSteps][0] })
        : actionSteps.size === 1
          ? t('treatment_courses.stat_action_on_one_step', { n: [...actionSteps][0] })
          : t('treatment_courses.stat_action_on_steps', { count: actionSteps.size });
  const createdAt = course.started_at ?? course.created_at;

  return (
    <CourseTabPanel testId="treatment-course-overview">
      <CourseBreadcrumb
        items={[
          { label: t('treatment_courses.crumb_all'), onClick: onBack },
          { label: course.template_name || t('treatment_courses.summary_untitled') },
        ]}
      />
      <div className="tf-h">
        <div className="hgrp">
          <div className="hl">
            <h2 id="h-fl">{course.template_name || t('treatment_courses.summary_untitled')}</h2>
            <CourseStatusChip status={course.status} />
          </div>
          <p className="fl-sub num">
            {t('treatment_courses.list_from_template', {
              name: course.template_name || t('treatment_courses.summary_untitled'),
              steps: t('treatment_courses.step_count', { count: course.steps.length }),
            })}
            {createdAt
              ? ` · ${courseCreatedByClause(
                  course.booked_by,
                  memberNames,
                  formatCourseDate(course.created_at || createdAt, i18n.language),
                  (vars) => t('treatment_courses.created_by', vars),
                )}`
              : ''}
          </p>
        </div>
        <div className="r">
          {editable ? (
            <Button type="button" variant="secondary" onClick={() => onEditCourse(next?.id)}>
              <Pencil className="i sm" aria-hidden />
              {t('treatment_courses.edit_title')}
            </Button>
          ) : null}
          {editable && onCancelCourse ? (
            <MenuPopover
              title={t('treatment_courses.step_menu')}
              trigger={
                <PatientTabIconButton label={t('treatment_courses.step_menu')}>
                  <MoreHorizontal className="i" />
                </PatientTabIconButton>
              }
            >
              <MenuItem danger onClick={onCancelCourse}>
                {t('treatment_courses.patient_cancel_course')}
              </MenuItem>
            </MenuPopover>
          ) : null}
        </div>
      </div>

      <div className="kstrip">
        <div>
          <div className="k">{t('treatment_courses.stat_progress')}</div>
          <div className="v num">
            {t('treatment_courses.completed_of_total', {
              completed: progress.completed,
              total: progress.total,
            })}
          </div>
          <CourseSegmentBar
            total={progress.total}
            completed={progress.completed}
            currentIndex={Math.max(progress.n - 1, 0)}
          />
        </div>
        <div>
          <div className="k">{t('treatment_courses.stat_next')}</div>
          <div className="v num">
            {next?.starts_at
              ? isSameCourseDay(next.starts_at)
                ? `${t('treatment_courses.today_chip')} · ${formatCourseDateTime(next.starts_at, i18n.language).split(' · ')[1]}`
                : formatCourseDateTime(next.starts_at, i18n.language)
              : t('treatment_courses.book_not_booked')}
            {next?.starts_at && isSameCourseDay(next.starts_at) ? <CourseTodayChip /> : null}
          </div>
          {next ? (
            <div className="s">
              {t('treatment_courses.step_n', { n: next.sort_order })} ·{' '}
              {courseStepTitle(next, t('treatment_courses.step_no_service'))}
            </div>
          ) : null}
        </div>
        <div>
          <div className="k">{t('treatment_courses.stat_action')}</div>
          <div className={actions.length ? 'v warnv' : 'v'}>
            {actions.length ? <AlertTriangle className="i sm" aria-hidden /> : null}
            {t('treatment_courses.stat_action_count', { count: actions.length })}
          </div>
          <div className="s">{actionDetail}</div>
        </div>
        <div>
          <div className="k">{t('treatment_courses.stat_period')}</div>
          <div className="v num">{formatCoursePeriod(period.start, period.end, i18n.language) || '—'}</div>
          <div className="s">{t('treatment_courses.stat_practitioners', { count: practitioners.length })}</div>
        </div>
      </div>

      <div className="ov-grid">
        <div>
          <div className="sec-t">
            <h3>{t('treatment_courses.section_steps')}</h3>
            <CourseCount value={course.steps.length} />
          </div>
          <ol className="tl3">
            {course.steps.map((step, index) => {
              const visual = deriveCourseStepVisual(course.steps, step.id);
              const action = stepHasAction(actions, step.id);
              const window = courseStepWindow(course.steps, step);
              const inWindow =
                !window || !step.starts_at || isInstantInCourseWindow(step.starts_at, window);
              const previous = index > 0 ? course.steps[index - 1] : null;
              const isNext = next?.id === step.id;
              const lineDone = visual === 'done' || (isNext && index > 0);
              return (
                <Fragment key={step.id}>
                  {previous && previous.sort_order >= 1 && step.interval_value > 0 ? (
                    <li className={lineDone || visual === 'next' ? 'gap ln-done' : 'gap'}>
                      <span className="mk-g" />
                      <CourseIntervalStrip
                        label={courseIntervalAfterLabel(step, previous.sort_order, t)}
                        window={window}
                        startsAt={step.starts_at}
                      />
                    </li>
                  ) : null}
                  <li className={visual === 'done' ? 'ln-done' : undefined}>
                    <CourseStepMarker visual={visual} action={action && visual !== 'done'} />
                    <article className={isNext ? 'st-card next' : visual === 'done' ? 'st-card done' : 'st-card'}>
                      {isNext ? (
                        <div className="nb">
                          <Clock className="i sm" aria-hidden />
                          <span>
                            {t('treatment_courses.next_appointment_band', {
                              when: formatCourseDateTimeWithToday(
                                step.starts_at,
                                i18n.language,
                                t('treatment_courses.today_chip'),
                              ),
                            })}
                          </span>
                          {isSameCourseDay(step.starts_at) ? <CourseTodayChip /> : null}
                        </div>
                      ) : null}
                      <div className={isNext ? 'in' : undefined}>
                        <div className="st-h">
                          <div className="grow">
                            <div className="st-n">
                              {t('treatment_courses.step_of_total', {
                                n: step.sort_order,
                                total: course.steps.length,
                              })}
                              {index === 0 ? ` · ${t('treatment_courses.first_step')}` : ''}
                            </div>
                            <button
                              type="button"
                              className="st-t w-full border-0 bg-transparent p-0 text-left"
                              onClick={() => onOpenStep(step.id)}
                            >
                              {courseStepTitle(step, t('treatment_courses.step_no_service'))}
                            </button>
                          </div>
                          <div className="chips">
                            {action ? <CourseVisualChip visual="planned" action /> : null}
                            <CourseVisualChip visual={visual} />
                            <CoursePlannedBookingChip visual={visual} startsAt={step.starts_at} />
                            {editable ? (
                              <MenuPopover
                                title={t('treatment_courses.step_menu')}
                                trigger={
                                  <PatientTabIconButton label={t('treatment_courses.step_menu')}>
                                    <MoreHorizontal className="i" />
                                  </PatientTabIconButton>
                                }
                              >
                                <MenuItem onClick={() => onOpenStep(step.id)}>
                                  {t('treatment_courses.open_step')}
                                </MenuItem>
                                <MenuItem onClick={() => onEditCourse(step.id)}>
                                  {t('treatment_courses.edit_step')}
                                </MenuItem>
                                {onCancelStep && canCancelBookedStep(step.status) ? (
                                  <MenuItem onClick={() => onCancelStep(step)}>
                                    {t('treatment_courses.patient_cancel_step')}
                                  </MenuItem>
                                ) : null}
                                {onRemoveStep && course.steps.length > 1 ? (
                                  <MenuItem danger onClick={() => onRemoveStep(step)}>
                                    {t('treatment_courses.remove_step')}
                                  </MenuItem>
                                ) : null}
                              </MenuPopover>
                            ) : null}
                          </div>
                        </div>
                        <div className="facts2 num">
                          <span className="dt">
                            <Calendar className="i sm" aria-hidden />
                            {step.starts_at
                              ? step.ends_at
                                ? formatCourseDateTimeRange(step.starts_at, step.ends_at, i18n.language)
                                : formatCourseDateTime(step.starts_at, i18n.language)
                              : t('treatment_courses.book_not_booked')}
                          </span>
                          <span>
                            <Clock className="i sm" aria-hidden />
                            {t('treatment_courses.duration_minutes', { minutes: step.duration_minutes })}
                          </span>
                          <span>
                            <User className="i sm" aria-hidden />
                            {coursePersonNames(step.practitioners, memberNames) ||
                              t('appointments.unassigned')}
                          </span>
                          {deriveBookedByChannel(step.appointment_source) ? (
                            <span>
                              <Check className="i sm" aria-hidden />
                              {t(
                                `treatment_courses.booked_by_${deriveBookedByChannel(step.appointment_source)}`,
                              )}
                            </span>
                          ) : null}
                        </div>
                        <div className="chk">
                          <CourseMetaChip
                            kind="journal"
                            label={t('treatment_courses.overview_journal')}
                            value={
                              step.has_journal_note
                                ? t('treatment_courses.journal_done')
                                : visual === 'done'
                                  ? t('treatment_courses.journal_missing')
                                  : t('treatment_courses.journal_not_started')
                            }
                            warn={!step.has_journal_note && visual === 'done'}
                            ok={step.has_journal_note}
                          />
                          {step.questionnaires.map((row) => {
                            const q = deriveQuestionnaireState({
                              jobStatus: row.job_status,
                              scheduledFor: row.scheduled_for,
                              attemptedAt: row.attempted_at,
                              assignmentStatus: row.assignment_status,
                              sentAt: row.sent_at,
                              completedAt: row.completed_at,
                            });
                            const name = courseQuestionnaireName(
                              row,
                              i18n.language,
                              t('treatment_courses.q_unnamed'),
                            );
                            const unanswered = q.state === 'sent' || q.state === 'failed';
                            return (
                              <CourseMetaChip
                                key={`${row.automation_id ?? row.forms_template_id}-${row.phase}`}
                                kind="questionnaire"
                                label={name}
                                value={
                                  q.state === 'completed'
                                    ? t('treatment_courses.q_answered')
                                    : unanswered
                                      ? t('treatment_courses.q_unanswered')
                                      : questionnaireStatusLabel(q.state, t)
                                }
                                warn={UNANSWERED_QUESTIONNAIRES_REQUIRE_ACTION && unanswered}
                                iconWarn={unanswered && !UNANSWERED_QUESTIONNAIRES_REQUIRE_ACTION}
                                ok={q.state === 'completed'}
                              />
                            );
                          })}
                        </div>
                        {isNext || action ? (
                          <div className="st-act">
                            {isNext ? (
                              <Button type="button" onClick={() => onOpenStep(step.id)}>
                                <ArrowRight className="i sm" aria-hidden />
                                {t('treatment_courses.open_step')}
                              </Button>
                            ) : null}
                            {isNext && editable ? (
                              <Button type="button" variant="secondary" onClick={() => onEditCourse(step.id)}>
                                <Calendar className="i sm" aria-hidden />
                                {step.appointment_id
                                  ? t('treatment_courses.move_time')
                                  : t('treatment_courses.book_time')}
                              </Button>
                            ) : null}
                            {isNext && editable && onCancelStep && canCancelBookedStep(step.status) ? (
                              <Button type="button" variant="tertiary" onClick={() => onCancelStep(step)}>
                                <CalendarX className="i sm" aria-hidden />
                                {t('treatment_courses.cancel_time')}
                              </Button>
                            ) : null}
                            {action && !step.has_journal_note && visual === 'done' ? (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => (onWriteJournal ? onWriteJournal(step.id) : onOpenStep(step.id))}
                              >
                                <Pencil className="i sm" aria-hidden />
                                {t('treatment_courses.write_journal')}
                              </Button>
                            ) : null}
                            {step.questionnaires.some((row) => {
                              const q = deriveQuestionnaireState({
                                jobStatus: row.job_status,
                                assignmentStatus: row.assignment_status,
                                sentAt: row.sent_at,
                                completedAt: row.completed_at,
                              });
                              return q.state === 'sent' || q.state === 'failed';
                            }) ? (
                              <Button
                                type="button"
                                variant="tertiary"
                                size="sm"
                                onClick={() => {
                                  const row = step.questionnaires.find((item) => {
                                    const q = deriveQuestionnaireState({
                                      jobStatus: item.job_status,
                                      assignmentStatus: item.assignment_status,
                                      sentAt: item.sent_at,
                                      completedAt: item.completed_at,
                                    });
                                    return q.state === 'sent' || q.state === 'failed';
                                  });
                                  if (row) onRemindQuestionnaire?.(step, row.forms_template_id);
                                }}
                              >
                                <Bell className="i sm" aria-hidden />
                                {t('treatment_courses.send_reminder')}
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                        {!inWindow && step.starts_at ? (
                          <p className="hint" style={{ color: 'var(--amber-ink)', marginTop: 8 }}>
                            {t('treatment_courses.window_out')}
                          </p>
                        ) : null}
                      </div>
                    </article>
                  </li>
                </Fragment>
              );
            })}
          </ol>
          {editable && onAddStep ? (
            <div style={{ margin: '16px 0 0 44px' }}>
              <button type="button" className="add-step" onClick={onAddStep}>
                <Plus className="i sm" aria-hidden />
                {t('treatment_courses.add_step')}
              </button>
            </div>
          ) : null}
        </div>

        <aside className="aside">
          <section className="box warnbox">
            <h3>
              <AlertTriangle className="i sm" aria-hidden />
              {t('treatment_courses.stat_action')}
              <CourseCount value={actions.length} />
            </h3>
            {actions.length === 0 ? (
              <p className="mini-e">{t('treatment_courses.none')}</p>
            ) : (
              <ul className="att">
                {actions.map((item) => {
                  const step = course.steps.find((row) => row.id === item.stepId);
                  return (
                    <li key={item.id}>
                      {item.kind === 'missing_journal' ? (
                        <svg className="i sm" viewBox="0 0 24 24" aria-hidden>
                          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                          <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                        </svg>
                      ) : (
                        <AlertTriangle className="i sm" aria-hidden />
                      )}
                      <div>
                        <div className="t">{courseActionTitle(item, t)}</div>
                        <div className="m num">
                          {courseActionItemMeta(item, step, course.steps.length, i18n.language, t)}
                        </div>
                        <div className="a">
                          <button
                            type="button"
                            className="link"
                            onClick={() => {
                              if (item.kind === 'missing_journal' && onWriteJournal) {
                                onWriteJournal(item.stepId);
                                return;
                              }
                              if (item.kind === 'unanswered_questionnaire' && step && item.questionnaireId) {
                                onRemindQuestionnaire?.(step, item.questionnaireId);
                                return;
                              }
                              if (item.kind === 'unbooked') {
                                onEditCourse(item.stepId);
                                return;
                              }
                              onOpenStep(item.stepId);
                            }}
                          >
                            {item.kind === 'missing_journal'
                              ? t('treatment_courses.write_note')
                              : item.kind === 'unanswered_questionnaire'
                                ? t('treatment_courses.send_reminder')
                                : item.kind === 'unbooked'
                                  ? t('treatment_courses.book_time')
                                  : t('treatment_courses.open_step')}
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <section className="box">
            <h3>{t('treatment_courses.about_course')}</h3>
            <dl className="dl2 num">
              <div>
                <dt>{t('treatment_courses.about_template')}</dt>
                <dd>
                  {course.template_name}, {t('treatment_courses.step_count', { count: course.steps.length })}
                </dd>
              </div>
              <div>
                <dt>{t('treatment_courses.about_start')}</dt>
                <dd>{formatCourseDateTime(period.start ?? course.started_at, i18n.language) || '—'}</dd>
              </div>
              <div>
                <dt>{t('treatment_courses.about_end')}</dt>
                <dd>{formatCourseDate(period.end, i18n.language) || '—'}</dd>
              </div>
              <div>
                <dt>{t('treatment_courses.about_practitioners')}</dt>
                <dd>{practitioners.join(', ') || t('treatment_courses.none')}</dd>
              </div>
              <div>
                <dt>{t('treatment_courses.about_punch')}</dt>
                <dd>{t('treatment_courses.about_punch_none')}</dd>
              </div>
            </dl>
          </section>
          <section className="box">
            <h3>{t('treatment_courses.legend_title')}</h3>
            <div className="legend2">
              <span>
                <CourseStepMarker visual="done" size="sm" />
                {t('treatment_courses.visual_done')}
              </span>
              <span>
                <CourseStepMarker visual="next" size="sm" />
                {t('treatment_courses.visual_next')}
              </span>
              <span>
                <CourseStepMarker visual="planned" size="sm" />
                {t('treatment_courses.visual_planned')}
              </span>
              <span>
                <CourseStepMarker visual="planned" size="sm" action />
                {t('treatment_courses.visual_action')}
              </span>
            </div>
          </section>
        </aside>
      </div>
    </CourseTabPanel>
  );
}
